import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Cpu, Lock, BookOpen, Phone, MapPin, Star, TrendingUp, Users, Activity, Microscope, ChevronLeft, ChevronRight, HeartPulse, UserRoundCheck, Zap } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { clinicData } from '../data';
import AnimatedSlider from '../components/ui/AnimatedSlider';
import ServicesSection from '../components/ServicesSection';

const Home = () => {
  const [currentStat, setCurrentStat] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % clinicData.statistics.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleWhatsAppCall = () => {
    window.open(`https://wa.me/91${clinicData.contact.whatsappNumber}?text=Hello, I would like to book an appointment`, '_blank');
  };

  const iconMap = {
    Award, Cpu, Lock, BookOpen, Microscope, Users, Activity, TrendingUp, HeartPulse, UserRoundCheck, Zap
  };

  // Define slides for the animated slider
  const sliderSlides = [
    {
      image: "/images/hero_clinic_1768065648396.png",
      category: "Specialized Care",
      title: "Men's Health & Andrology",
      description: "Helping men achieve better sexual and reproductive health using proven and innovative methods",
      buttonText: "Our Services",
      onClick: () => window.location.hash = "/treatments"
    },
    {
      image: "https://images.pexels.com/photos/7578803/pexels-photo-7578803.jpeg?w=1200&q=80",
      category: "Expert Doctors",
      title: "Team of Specialists",
      description: "Experienced Genitourinary Surgeons providing comprehensive care with confidentiality",
      buttonText: "Meet Our Doctors",
      onClick: () => window.location.hash = "/about"
    },
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section with Full-Width Animated Slider */}
      <section className="relative pt-24 pb-0 px-0 bg-gradient-to-br from-blue-50 via-white to-pink-50 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#D91656]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-white to-transparent"></div>

        <div className="w-full relative z-10">
          <AnimatedSlider slides={sliderSlides} autoPlay={true} interval={6000} />
        </div>
      </section>

      {/* Special Technologies Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#1E5A8E] to-[#D91656]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Special Technologies
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              State-of-the-art treatments available only at MAN CLINICS
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {clinicData.specialTechnologies.map((tech, index) => {
              const IconComponent = index === 0 ? Zap : Activity;
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-colors">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4">
                    <IconComponent className="h-8 w-8 text-[#D91656]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{tech.name}</h3>
                  <p className="text-white/80">{tech.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Doctors Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#1E5A8E]/10 text-[#1E5A8E] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Doctors
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Meet Our <span className="text-[#D91656]">Specialists</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A team of highly qualified Genitourinary Surgeons dedicated to men's health
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {clinicData.doctors.map((doctor, index) => (
              <Card key={index} className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    onError={(e) => {
                      e.target.src = `https://placehold.co/400x500/e2e8f0/64748b/?text=${encodeURIComponent(doctor.name.split(' ')[1] || 'Doctor')}`;
                      e.target.onerror = null;
                    }}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{doctor.name}</h3>
                  <p className="text-[#D91656] font-medium text-sm mb-1">{doctor.qualifications}</p>
                  <p className="text-gray-600 text-sm">{doctor.designation}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              className="bg-[#D91656] hover:bg-[#B01348] text-white"
              asChild
            >
              <Link to="/about">View All Doctors</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Clinic Features Carousel */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#1E5A8E]/10 text-[#1E5A8E] px-4 py-2 rounded-full text-sm font-medium mb-4">
              Our Facilities
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              World-Class <span className="text-[#D91656]">Healthcare Facilities</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              State-of-the-art medical equipment and comfortable environment for your complete care
            </p>
          </div>

          <Carousel className="w-full max-w-6xl">
            <CarouselContent>
              {clinicData.clinicFeatures.map((feature, index) => {
                const IconComponent = iconMap[feature.icon] || Award;
                return (
                  <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                    <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          onError={(e) => {
                            e.target.src = `https://placehold.co/600x400/e2e8f0/64748b/?text=${encodeURIComponent(feature.title)}`;
                            e.target.onerror = null;
                          }}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4">
                          <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                            <IconComponent className="h-6 w-6 text-[#D91656]" />
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious className="left-0" />
            <CarouselNext className="right-0" />
          </Carousel>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-[#D91656]">MAN CLINICS</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your trusted partner in men's health and wellness
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {clinicData.whyChooseUs.map((reason, index) => {
              const IconComponent = iconMap[reason.icon] || Award;
              return (
                <div key={index} className="text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#D91656]/10 to-[#1E5A8E]/10 rounded-full">
                    <IconComponent className="h-8 w-8 text-[#D91656]" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <ServicesSection services={clinicData.services} />
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#1E5A8E] to-[#D91656]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Take Control of Your Health?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Schedule a confidential consultation with our specialists today. Your health and privacy are our top priorities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsAppCall}
              size="lg"
              className="bg-white text-[#D91656] hover:bg-gray-100 text-lg px-8 py-6"
            >
              Book Appointment via WhatsApp
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              onClick={() => window.location.href = `tel:${clinicData.contact.phone}`}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call {clinicData.contact.phone}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

